import { toast } from 'sonner';

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
export const SERVER_TIMEOUT_MESSAGE = 'The server took too long to respond. Please try again, or choose a shorter date range.';
export const PDF_GENERATION_FAILED_MESSAGE = "We couldn't generate the PDF. Please try again. If it keeps failing, export to Excel or CSV instead.";
export const PDF_NOT_AVAILABLE_MESSAGE = "This PDF isn't available right now. Please try again in a moment.";

const PDF_SIGNATURE = '%PDF-';
const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const GATEWAY_STATUSES = [502, 503, 504];

// type tag instead of instanceof, which fails for buffers created in another realm (iframes, test environments)
const isArrayBuffer = (value) => Object.prototype.toString.call(value) === '[object ArrayBuffer]';

const hasPdfSignature = (buffer) => String.fromCharCode(...new Uint8Array(buffer, 0, Math.min(buffer.byteLength, PDF_SIGNATURE.length))) === PDF_SIGNATURE;

/**
 * Error bodies of download requests (responseType 'arraybuffer') arrive as raw bytes, so the API's
 * `message` can't be read. Decode them back into the JSON object every other request gets, and fill in
 * a message when there is none (e.g. an HTML page from the proxy when the server timed out).
 *
 * @param {import('axios').AxiosResponse} response - Failed response; its `data` is replaced in place
 */
export const normalizeErrorResponse = (response) => {
    let data = response.data;
    if (isArrayBuffer(data)) {
        try {
            data = JSON.parse(new TextDecoder().decode(data));
        } catch {
            data = {};
        }
    }
    if (!data || typeof data !== 'object') data = {};

    if (!data.message && response.status >= 500) {
        data = { ...data, message: GATEWAY_STATUSES.includes(response.status) ? SERVER_TIMEOUT_MESSAGE : GENERIC_ERROR_MESSAGE };
    }
    response.data = data;
};

const triggerBrowserDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Save a successful export response (PDF, Excel or CSV) as a file.
 * A body that isn't the file it claims to be (e.g. "{}" labelled as a PDF) is not saved: the user is told instead.
 *
 * @param {import('axios').AxiosResponse} resp - Response of a request made with responseType 'arraybuffer'
 * @param {string} fallbackFilename - Used when the server sends no Content-Disposition filename
 * @returns {boolean} Whether a file was saved
 */
export const saveFileFromResponse = (resp, fallbackFilename) => {
    const contentType = resp?.headers?.['content-type'] || '';
    const isPdf = contentType.includes('application/pdf');
    const isSpreadsheet = contentType.includes(XLSX_CONTENT_TYPE) || contentType.includes('text/csv');

    if (!isArrayBuffer(resp?.data) || (!isPdf && !isSpreadsheet) || (isPdf && !hasPdfSignature(resp.data))) {
        toast.error(isSpreadsheet ? GENERIC_ERROR_MESSAGE : PDF_GENERATION_FAILED_MESSAGE);
        return false;
    }

    const contentDisposition = resp.headers['content-disposition'];
    const filename = contentDisposition?.includes('filename=') ? contentDisposition.split('filename=')[1].replace(/"/g, '') : fallbackFilename;
    triggerBrowserDownload(new Blob([resp.data], { type: contentType }), filename);
    return true;
};

/**
 * Download a stored PDF (CloudFront/S3 link). A missing file comes back as an XML error page,
 * which must not be saved as a ".pdf".
 *
 * @param {string} url
 * @param {string} filename
 * @returns {Promise<boolean>} Whether a file was saved
 */
export const downloadPdfFromUrl = async (url, filename) => {
    if (!url) {
        toast.error(PDF_NOT_AVAILABLE_MESSAGE);
        return false;
    }
    try {
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        if (!response.ok || !hasPdfSignature(buffer)) {
            toast.error(PDF_NOT_AVAILABLE_MESSAGE);
            return false;
        }
        triggerBrowserDownload(new Blob([buffer], { type: 'application/pdf' }), filename);
        return true;
    } catch (error) {
        console.error('Error downloading PDF:', error);
        toast.error(PDF_NOT_AVAILABLE_MESSAGE);
        return false;
    }
};

/**
 * Open a stored PDF in a new tab, or tell the user it isn't there instead of opening a blank tab.
 *
 * @param {string} url
 * @returns {boolean} Whether a tab was opened
 */
export const openPdfUrl = (url) => {
    if (!url) {
        toast.error(PDF_NOT_AVAILABLE_MESSAGE);
        return false;
    }
    window.open(url, '_blank');
    return true;
};
